import {
  get,
  find,
  forEach,
  cloneDeep,
  sortBy,
  isPlainObject,
} from 'lodash-es'
import DateUtils from './date-utils'
import AddressUtils from './address-utils'
import appProps from '../app-props'

const useByEads = appProps.get('checkout.audit.use.by.ead', false)

const MEMBERSHIP_SKU = '6485920018'
const MEMBERSHIP_REPLACEMENT_SKU = 'REImember'

class ItemUtils {
  static get MEMBERSHIP_SKU() {
    return MEMBERSHIP_SKU
  }

  static get MEMBERSHIP_REPLACEMENT_SKU() {
    return MEMBERSHIP_REPLACEMENT_SKU
  }

  /**
   * checks to see if an "item" is a gift card. This can include
   * cards passed in which have a different model than orderItems.
   * @param  {Object}  item Either an orderItem or a card from the GC service
   * @return {Boolean}      true/false on if the item is a giftcard.
   */
  static isGiftCard(item) {
    return !!(get(item, 'giftCardDetails.mediaCode')
      || get(item, 'typeCode')
      || get(item, 'type'))
  }

  // To identify gift card formats check the first letter of the mediaCode:
  // 'G' === Physical
  // 'E' === Digitial
  // 'P' === Promo Card (which will also be digital)
  static isPhysicalGiftCard(item) {
    if (!this.isGiftCard(item)) return false
    return !!(get(item, 'giftCardDetails.mediaCode', '').charAt(0) === 'G'
      || get(item, 'typeCode', '') === 'GCD'
      || get(item, 'type', '') === 'GIFTCARD'
      || get(item, 'typeCode', '') === 'RMC'
      || get(item, 'type', '') === 'MERCH_CREDIT')
  }

  static isDigitalGiftCard(item) {
    if (!this.isGiftCard(item)) return false
    return !!(get(item, 'giftCardDetails.mediaCode', '').charAt(0) === 'E'
      || get(item, 'typeCode', '') === 'GC'
      || get(item, 'type', '') === 'GIFTCERT')
  }

  static isPromoGiftCard(item) {
    if (!this.isGiftCard(item)) return false
    return !!(get(item, 'giftCardDetails.mediaCode', '').charAt(0) === 'P'
      || get(item, 'typeCode', '') === 'PGCD'
      || get(item, 'type', '') === 'PROMO_GCD')
  }

  static isMABC(item) {
    if (!this.isGiftCard(item)) return false
    return !!(get(item, 'giftCardDetails.promoCardType', '') === 'MABC')
  }

  static isActiveGCorBC(card) {
    return card.balanceStatus === 'ACTIVE' || card.status === 'INQUIRE_SUCCESS'
  }

  static hasBalanceGCorBC(card) {
    return card.balance > 0
  }

  static isMembership(item) {
    return get(item, 'sku', '') === this.MEMBERSHIP_SKU
  }

  static isJustForMembersItem(item) {
    if (!!item.badges && item.badges.length > 0) {
      return item.badges[0].title === 'Just for Members'
    }
    return false
  }

  static isBackorderPreorderItem(item) {
    return this.isPreorderItem(item) || this.isBackorderItem(item)
  }

  static isPreorderItem(item) {
    return item.skuStatus === 'PRE_ORDER'
  }

  static isBackorderItem(item) {
    if (this.isPreorderItem(item)) return false
    // An item will have this fulfillmentInfo property when we break them out by fulfillment on
    // the review page
    if (get(item, 'fulfillmentInfo.backordered', null)) return true

    let isBackorder = false
    const fulfillments = get(item, 'fulfillment.fulfillmentInfos')
      ? get(item, 'fulfillment.fulfillmentInfos', []) : get(item, 'fulfillments.allowedShipMethods[0].fulfillmentInfos', [])

    fulfillments.forEach((fulfillment) => {
      if (fulfillment.backordered) isBackorder = true
    })

    return isBackorder
  }

  static getBackorderedInventory(item) {
    let qty = 0
    let fulfillmentInfos

    // First check if the item has been grouped by EAD and has `fulfillmentInfo` attached
    if (get(item, 'fulfillmentInfo')) {
      fulfillmentInfos = get(item, 'fulfillmentInfo')
    // If not check if it has audit fulfillment data from Sterling
    } else if (get(item, 'fulfillment.fulfillmentInfos[0].ead') > 0) {
      fulfillmentInfos = get(item, 'fulfillment.fulfillmentInfos')
    // Else use alculated-available-shipping-options data
    } else {
      fulfillmentInfos = get(item, 'fulfillments.allowedShipMethods[0].fulfillmentInfos', [])
    }

    fulfillmentInfos.forEach((fulfillmentInfo) => {
      if (fulfillmentInfo.backordered) {
        qty += fulfillmentInfo.fulfillmentQty
      }
    })

    return qty
  }

  static isCompletelyBackordered(item) {
    return this.getBackorderedInventory(item) === item.quantity
  }

  /**
  * Determines if the item is eligible to display
  * a fallback ead in the case that fulfillment is
  * overwhelmed or turned off.
  * @returns {Boolean}
  */
  static hasFallbackEad(item) {
    const shipMethod = get(item, 'shipMethod.id', '')
    const shippingAddress = get(item, 'shippingAddress.address', {})
    const isOversized = get(item, 'oversized', false)
    const isHazardous = get(item, 'hazardous', false)

    return (shipMethod === 'STANDARD' || shipMethod === 'GIFT_CARD_AND_MEMBERSHIP')
      && !this.isBackorderPreorderItem(item)
      && shippingAddress
      && (!AddressUtils.isUnavailableEadAddress(shippingAddress) && !useByEads)
      && !isOversized
      && !isHazardous
  }

  static filterByEAD(items, options) {
    const eadItems = []
    const context = options ? options.context : null
    const shipMethod = options ? options.shipMethod : null

    items.forEach((item) => {
      const filteredFulfillments = {}
      const allowedShipMethods = get(item, 'fulfillments.allowedShipMethods', [])
      let fulfillments

      if (context !== 'ship-or-pickup-page' && get(item, 'fulfillment.fulfillmentInfos', null)) {
        fulfillments = get(item, 'fulfillment.fulfillmentInfos', [])
      } else if (allowedShipMethods.length && shipMethod) {
        const shipMethodInfo = find(allowedShipMethods, sm => sm.id === shipMethod)
        fulfillments = shipMethodInfo ? shipMethodInfo.fulfillmentInfos : []
      } else {
        fulfillments = get(item, 'fulfillments.allowedShipMethods[0].fulfillmentInfos', [])
      }

      if (fulfillments && fulfillments.length) {
        fulfillments.forEach((fulfillment) => {
          const date = new Date(fulfillment.ead)
          const backorderStatus = fulfillment.backordered
          // There can be multiple fulfillments arriving at different times on the same day.
          // For displaying the EAD on the UI we only care about the day and not the time so
          // all EADs for the same day but different times are grouped together.
          const key = `${date.getUTCMonth()}-${date.getUTCDate()}-${backorderStatus}`
          if (!filteredFulfillments[key]) {
            filteredFulfillments[key] = {
              backordered: fulfillment.backordered,
              ead: fulfillment.ead,
              fulfillmentQty: fulfillment.fulfillmentQty,
              shipNodes: [fulfillment.shipNode],
            }
          } else {
            filteredFulfillments[key].fulfillmentQty += fulfillment.fulfillmentQty
            filteredFulfillments[key].shipNodes.push(fulfillment.shipNode)
          }
        })
        forEach(filteredFulfillments, (fulfillment) => {
          const eadItem = cloneDeep(item)
          const quantity = fulfillment.fulfillmentQty
          const adjustedUnitPrice = eadItem.price.unitAdjusted
          const unitPrice = eadItem.price.original
          const totalDiscount = (eadItem.price.original - adjustedUnitPrice) * quantity

          // Adjust prices and quantity to reflect EAD grouping rather than whole line item
          eadItem.quantity = quantity
          eadItem.price.totalAdjusted = quantity * adjustedUnitPrice
          eadItem.price.totalOriginal = quantity * unitPrice
          eadItem.price.totalDiscount = totalDiscount < 0 ? 0 : totalDiscount
          eadItem.fulfillmentInfo = fulfillment
          eadItems.push(eadItem)
        })
      } else {
        // No fulfillment information for item
        const eadItem = cloneDeep(item)
        eadItem.fulfillmentInfo = { ead: -1 } // fulfill the sort
        eadItems.push(eadItem)
      }
    })
    return sortBy(eadItems, item => item.fulfillmentInfo.ead)
  }

  /**
   * Takes an array of line items and checks if any have an EAD of TODAY.
   * Note: the input line items should be filtered by EAD first via `filterByEAD`
   * @param {Array} items Store pick up line items
   * @returns {Boolean}
   */
  static hasPickupTodayItems(items) {
    for (let i = 0, l = items.length; i < l; i += 1) {
      const ead = get(items[i], 'fulfillmentInfo.ead', 0)
      if (DateUtils.isEadToday(ead)) {
        return true
      }
    }

    return false
  }

  // "type": "PROMO_GCD", == one time use
  static isSingleUseGC(gcTender) {
    return (get(gcTender, 'type') === 'PROMO_GCD'
      || get(gcTender, 'typeCode') === 'PGCD')
  }

  /**
   * Takes an array of line items and checks if all of them
   * are eligible to show the fallback EAD if fulfillment is
   * turned off or overwhelmed.
   * @param {Array} items Line items.
   * @returns {Boolean}
   */
  static allItemsHaveFallbackEad(items, shippingMethod) {
    if (!items || !shippingMethod) return false
    return items.every((item) => {
      const shippingAddress = get(item, 'shippingAddress.address', {})
      const isOversized = get(item, 'oversized', false)
      const isHazardous = get(item, 'hazardous', false)

      return (shippingMethod === 'STANDARD' || shippingMethod === 'GIFT_CARD_AND_MEMBERSHIP')
        && !this.isBackorderPreorderItem(item)
        && shippingAddress
        && (!AddressUtils.isUnavailableEadAddress(shippingAddress) && !useByEads)
        && !isOversized
        && !isHazardous
    })
  }

  /**
   * Checks to see if a line item has a shipping address
   * @param  {Object}  lineItem a single line item
   * @return {Boolean}          true if the item has an address, false if no address.
   */
  static hasShippingAddress(lineItem) {
    if (!isPlainObject(lineItem)) throw new Error('method `hasShippingAddress` requires `lineItem` argument of type object')

    let addressNotEmpty = false
    const itemShipAddress = get(lineItem, 'shippingAddress.address')
    if (itemShipAddress) { addressNotEmpty = AddressUtils.isNotEmpty(itemShipAddress) }

    return (itemShipAddress && addressNotEmpty)
  }
}

export default ItemUtils
